import { DragEvent, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { urlFile } from '../../../../AxiosConfig';
import { ChevronLeft, ChevronRight, Plus, Trash2, X } from 'lucide-react';

export const GALLERY_ACCEPT = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

type PaginaGaleriaProps = {
    imagesIds: string[];
    pendingUploads: number;
    disabled: boolean;
    onAddFiles: (files: File[]) => void;
    onRemove: (index: number) => void;
};

const imageUrl = (id: string) => `${urlFile}/${encodeURIComponent(id)}`;

export function PaginaGaleria({ imagesIds, pendingUploads, disabled, onAddFiles, onRemove }: PaginaGaleriaProps) {
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [dragging, setDragging] = useState(false);
    const [previewIndex, setPreviewIndex] = useState<number | null>(null);

    const openPicker = () => {
        if (!disabled) inputRef.current?.click();
    };

    const handleDrop = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setDragging(false);
        if (disabled) return;
        const files = Array.from(event.dataTransfer.files || []);
        if (files.length) onAddFiles(files);
    };

    // Teclado na visualizacao ampliada: Esc fecha, setas navegam.
    useEffect(() => {
        if (previewIndex === null) return;
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setPreviewIndex(null);
            if (event.key === 'ArrowRight') setPreviewIndex((index) => (index === null ? index : (index + 1) % imagesIds.length));
            if (event.key === 'ArrowLeft')
                setPreviewIndex((index) => (index === null ? index : (index - 1 + imagesIds.length) % imagesIds.length));
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [previewIndex, imagesIds.length]);

    useEffect(() => {
        if (previewIndex !== null && previewIndex >= imagesIds.length) {
            setPreviewIndex(imagesIds.length ? imagesIds.length - 1 : null);
        }
    }, [imagesIds.length, previewIndex]);

    return (
        <div
            className="relative rounded-[var(--sf-radius-md)] p-3 sm:p-4"
            onDragOver={(event) => {
                event.preventDefault();
                if (!disabled) setDragging(true);
            }}
            onDragLeave={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget as Node)) setDragging(false);
            }}
            onDrop={handleDrop}
        >
            <input
                ref={inputRef}
                id="pageGallery"
                type="file"
                multiple
                accept={GALLERY_ACCEPT.join(',')}
                className="hidden"
                onChange={(event) => {
                    const files = Array.from(event.target.files || []);
                    event.target.value = '';
                    if (files.length) onAddFiles(files);
                }}
            />

            <ul className="grid grid-cols-3 gap-1.5 sm:grid-cols-4 sm:gap-2 lg:grid-cols-5 2xl:grid-cols-6">
                <li>
                    <button
                        type="button"
                        onClick={openPicker}
                        disabled={disabled}
                        className="estab-gallery-add flex aspect-square w-full flex-col items-center justify-center gap-1.5 rounded-[0.625rem] text-[rgb(var(--sf-nav-accent-rgb))] disabled:opacity-40"
                        aria-label="Adicionar fotos"
                    >
                        <Plus className="text-lg" aria-hidden="true" />
                        <span className="text-xs font-medium">Adicionar</span>
                    </button>
                </li>

                {imagesIds.map((id, index) => (
                    <li key={`${id}-${index}`} className="group relative">
                        <button
                            type="button"
                            className="block aspect-square w-full overflow-hidden rounded-[0.625rem] bg-base-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[rgb(var(--sf-nav-accent-rgb))]"
                            onClick={() => setPreviewIndex(index)}
                            aria-label={`Ampliar foto ${index + 1}`}
                        >
                            <img
                                src={imageUrl(id)}
                                alt={`Foto ${index + 1} da galeria`}
                                loading="lazy"
                                className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
                            />
                        </button>
                        <button
                            type="button"
                            disabled={disabled}
                            onClick={() => onRemove(index)}
                            aria-label={`Remover foto ${index + 1}`}
                            className="absolute right-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-black/55 text-[0.65rem] text-white backdrop-blur-md transition-opacity hover:bg-black/70 disabled:opacity-0 sm:opacity-0 sm:focus-visible:opacity-100 sm:group-hover:opacity-100"
                        >
                            <X aria-hidden="true" />
                        </button>
                    </li>
                ))}

                {Array.from({ length: pendingUploads }).map((_, index) => (
                    <li key={`pending-${index}`} aria-hidden>
                        <div className="sf-skeleton flex aspect-square w-full items-center justify-center rounded-[0.625rem]">
                            <span className="loading loading-spinner loading-sm text-base-content/40"></span>
                        </div>
                    </li>
                ))}
            </ul>

            {!imagesIds.length && !pendingUploads && (
                <p className="mt-3 text-center text-xs text-base-content/50">
                    Nenhuma foto ainda. Toque em Adicionar ou arraste imagens para cá.
                </p>
            )}

            {pendingUploads > 0 && (
                <p role="status" className="mt-3 text-xs text-base-content/55">
                    Enviando {pendingUploads} {pendingUploads === 1 ? 'foto' : 'fotos'}…
                </p>
            )}

            {dragging && (
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-[inherit] bg-[rgb(var(--sf-nav-accent-rgb)/0.08)] ring-2 ring-inset ring-[rgb(var(--sf-nav-accent-rgb)/0.6)]">
                    <span className="rounded-full bg-base-100 px-3 py-1.5 text-sm font-medium shadow">Solte para adicionar</span>
                </div>
            )}

            {/* Portal: evita que transform/blur de ancestrais limite o overlay fixo. */}
            {previewIndex !== null && imagesIds[previewIndex] && createPortal(
                <div
                    className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/90 backdrop-blur-sm"
                    role="dialog"
                    aria-modal="true"
                    aria-label={`Foto ${previewIndex + 1} de ${imagesIds.length}`}
                    onClick={() => setPreviewIndex(null)}
                >
                    <img
                        src={imageUrl(imagesIds[previewIndex])}
                        alt={`Foto ${previewIndex + 1} da galeria`}
                        className="max-h-[85vh] max-w-[92vw] rounded-lg object-contain"
                        onClick={(event) => event.stopPropagation()}
                    />

                    <div className="absolute left-0 right-0 top-0 flex items-center justify-between p-4 text-white">
                        <span className="text-sm text-white/70">
                            {previewIndex + 1} de {imagesIds.length}
                        </span>
                        <button
                            type="button"
                            className="flex h-9 w-9 items-center justify-center rounded-full bg-white/15 hover:bg-white/25"
                            onClick={() => setPreviewIndex(null)}
                            aria-label="Fechar"
                        >
                            <X aria-hidden="true" />
                        </button>
                    </div>

                    {imagesIds.length > 1 && (
                        <>
                            <button
                                type="button"
                                className="absolute left-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/15 text-white hover:bg-white/25"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    setPreviewIndex((previewIndex - 1 + imagesIds.length) % imagesIds.length);
                                }}
                                aria-label="Foto anterior"
                            >
                                <ChevronLeft aria-hidden="true" />
                            </button>
                            <button
                                type="button"
                                className="absolute right-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/15 text-white hover:bg-white/25"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    setPreviewIndex((previewIndex + 1) % imagesIds.length);
                                }}
                                aria-label="Próxima foto"
                            >
                                <ChevronRight aria-hidden="true" />
                            </button>
                        </>
                    )}

                    <div className="absolute bottom-0 left-0 right-0 flex justify-center p-5">
                        <button
                            type="button"
                            disabled={disabled}
                            className="rounded-full bg-white/15 px-4 py-2 text-sm font-medium text-[#FF453A] hover:bg-white/25 disabled:opacity-40"
                            onClick={(event) => {
                                event.stopPropagation();
                                onRemove(previewIndex);
                            }}
                        >
                            <Trash2 className="mr-2" aria-hidden="true" />
                            Remover foto
                        </button>
                    </div>
                </div>,
                document.body,
            )}
        </div>
    );
}
